import type { Calendario } from '@/types';

/** Chave do navegador onde fica lembrado o filtro de empresas da agenda única. */
export const AGENDA_EMPRESAS_STORAGE_KEY = 'kora:agenda:empresas';

export interface GrupoCalendariosEmpresa {
  empresaId: string;
  empresaNome: string;
  calendarios: Calendario[];
}

/** Id da empresa dona do calendário (campo da própria linha, ou o objeto `empresa` da agenda única). */
export function empresaIdDoCalendario(calendario: Pick<Calendario, 'empresa_id' | 'empresa'>): string {
  return calendario.empresa?.id || calendario.empresa_id || '';
}

export function empresaNomeDoCalendario(calendario: Pick<Calendario, 'empresa_id' | 'empresa'>): string {
  return calendario.empresa?.nome || '';
}

/**
 * Agrupa calendários por empresa (ordem alfabética das empresas; a ordem dos calendários dentro de
 * cada grupo é preservada). `empresaAtivaId`, quando informada, vem primeiro.
 */
export function agruparCalendariosPorEmpresa(
  calendarios: Calendario[],
  nomesPorEmpresaId: Record<string, string> = {},
  empresaAtivaId?: string,
): GrupoCalendariosEmpresa[] {
  const grupos = new Map<string, GrupoCalendariosEmpresa>();
  for (const calendario of calendarios) {
    const empresaId = empresaIdDoCalendario(calendario);
    let grupo = grupos.get(empresaId);
    if (!grupo) {
      grupo = {
        empresaId,
        empresaNome: empresaNomeDoCalendario(calendario) || nomesPorEmpresaId[empresaId] || 'Empresa',
        calendarios: [],
      };
      grupos.set(empresaId, grupo);
    }
    grupo.calendarios.push(calendario);
  }
  return [...grupos.values()].sort((a, b) => {
    if (empresaAtivaId) {
      if (a.empresaId === empresaAtivaId && b.empresaId !== empresaAtivaId) return -1;
      if (b.empresaId === empresaAtivaId && a.empresaId !== empresaAtivaId) return 1;
    }
    return a.empresaNome.localeCompare(b.empresaNome, 'pt-BR');
  });
}

/** Há calendários de mais de uma empresa na lista? (decide se agrupa/mostra selo de empresa) */
export function temMaisDeUmaEmpresa(calendarios: Calendario[]): boolean {
  return new Set(calendarios.map(empresaIdDoCalendario)).size > 1;
}

/** Lê do navegador o filtro de empresas lembrado (null = sem escolha salva = todas). */
export function lerFiltroEmpresasSalvo(): string[] | null {
  try {
    const bruto = localStorage.getItem(AGENDA_EMPRESAS_STORAGE_KEY);
    if (!bruto) return null;
    const lista: unknown = JSON.parse(bruto);
    if (!Array.isArray(lista)) return null;
    const ids = lista.filter((id): id is string => typeof id === 'string');
    return ids.length > 0 ? ids : null;
  } catch {
    return null;
  }
}

/** Guarda no navegador o filtro de empresas (null/vazio = todas, apaga a escolha). */
export function salvarFiltroEmpresas(ids: string[] | null): void {
  try {
    if (!ids || ids.length === 0) localStorage.removeItem(AGENDA_EMPRESAS_STORAGE_KEY);
    else localStorage.setItem(AGENDA_EMPRESAS_STORAGE_KEY, JSON.stringify(ids));
  } catch { /* ignore */ }
}
